<template>
  <div class="popUpWrap">
    <section class="popup dark assessment">
      <div class="topArea">
        <div class="exit" @click.stop="onClickClose"></div>
      </div>
      <div class="scrollY">
        <div class="textArea flex">
          <h5>{{ t('skillCertify-environment-check-help-title') }}</h5>
        </div>

        <!--
          BR-1762: 항목별로 박스를 나눈다. 예전에는 카메라·마이크·화면 녹화 안내가 불릿 하나의
          평평한 목록이라, 실패한 항목이 무엇이든 다섯 줄을 통째로 읽어야 자기 문제를 찾을 수 있었다.
          점검 결과 화면의 항목 구분(카메라/마이크 · 화면 녹화)과 같은 단위로 묶어 대응시킨다.
        -->
        <div class="boxArea">
          <div class="titleArea">
            <span aria-hidden="true">🎙️</span>{{ deviceSectionTitle }}
          </div>
          <ul class="listDot">
            <li>{{ t('skillCertify-proctoring-recording-fail-description') }}</li>
          </ul>
        </div>

        <div class="boxArea">
          <div class="titleArea">
            <span aria-hidden="true">🖥️</span>{{ t('skillMonitor-candidate-video-screen-recording') }}
          </div>
          <ul class="listDot">
            <!--
              macOS 만 시스템 권한 설정 경로를 안내한다(Proctive 앱을 체크하고 앱을 재시작해야 한다).
              Windows 는 그 설정이 없어 남는 원인이 차단 프로그램뿐이다.
            -->
            <li v-if="externalDirectorService.isMac"
                v-html="convertToNextLine(t('skillCertify-proctoring-entire-screen-recording-no-access-description-1') + '<br/>' + '<span>' + t('learning-assessment-guide-line-description-for-mac-2') + '</span>' + '<span>' + t('learning-assessment-guide-line-description-for-mac-4') + '</span>')"></li>
            <li>{{ t('skillCertify-proctoring-entire-screen-recording-no-access-description-2') }}</li>
            <!-- 운영팀 확정 링크를 받기 전까지 비활성 — environmentHelpLink.ts 참고. -->
            <li v-if="isHelpDetailEnabled" v-html="helpDetailHtml"></li>
          </ul>
        </div>

        <p class="contactText">{{ t('skillCertify-environment-check-contact-channel-talk') }}</p>
        <div class="btnArea">
          <button @click.stop="onClickClose">{{ t('common-ok') }}</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * ASMT-PROCTOR-15 (TSK-11690) — Proctive 응시 환경 점검 실패 시 종합 해결 안내.
 *
 * 항목별 팝업이 아니라 카메라·마이크·화면 녹화를 한 번에 안내하는 단일 팝업이다
 * (기능 정의서 §6: "하나라도 실패 시 [해결 방법 보기] 단일 버튼 → 종합 안내 팝업").
 *
 * BR-1762: 목업(「Proctive_해결방법안내필요시_2_팝업」)대로 항목별 박스로 나누고, 하단 버튼은
 * [확인] 하나만 둔다. 도움말은 화면 녹화 박스 마지막 줄의 인라인 링크가 담당한다.
 */
import { computed } from 'vue';
import { useDialogStore } from "~/store/dialog/dialogStore";
import { convertToNextLine, useI18n } from "#imports";
import { useExternalDirectorService } from "~/composables/service/useExternalDirectorService";
import {
  buildHelpDetailHtml,
  IS_HELP_DETAIL_ENABLED,
} from "~/components/Common/Dialog/dialog/Assessment/environmentHelpLink";

const { t } = useI18n();
const externalDirectorService = useExternalDirectorService();
const dialogStore = useDialogStore();

/**
 * "카메라/마이크" — 두 장치는 하나의 스트림으로 함께 잡히고 조치도 같으므로 한 박스에 묶는다.
 * 전용 키를 새로 만들지 않고 점검 결과 화면이 쓰는 항목명을 그대로 이어 붙인다 —
 * 같은 개념에 키가 둘이면 번역이 갈라진다(BR-1759 와 같은 이유).
 */
const deviceSectionTitle = computed(() =>
    `${t('skillCertify-proctoring-face-voice-recording-set-up-camera')}/${t('skillCertify-proctoring-face-voice-recording-set-up-microphone')}`
);

/** 확정 링크를 받기 전까지는 그리지 않는다 — `environmentHelpLink.ts` 의 되살리는 순서 참고. */
const isHelpDetailEnabled = IS_HELP_DETAIL_ENABLED;
const helpDetailHtml = computed(() => buildHelpDetailHtml(t));

const onClickClose = () => {
  dialogStore.close();
  dialogStore.options.close?.event();
};
</script>

<style scoped>
.contactText {
  margin-top: 16px;
}

/*
 * v-html 로 주입된 도움말 링크. scoped 속성이 붙지 않으므로 :deep() 로 내려보낸다.
 * 문장 흐름을 끊지 않도록 인라인으로 두고, 색·밑줄로만 링크임을 알린다.
 */
:deep(.helpLink) {
  color: #81ABFF;
  text-decoration: underline;
}

/*
 * 공용 `.popup.dark .boxArea` 는 한 덩어리 안내를 가운데 정렬하려고 flex row 다(specificity 0,3,0).
 * 여기서는 제목 줄과 목록을 세로로 쌓아야 하므로 `.popup.dark.assessment` 로 한 단계 더 좁혀
 * 확실히 이긴다 — 같은 specificity 로 두면 스타일 주입 순서에 결과가 달라진다.
 */
.popup.dark.assessment .boxArea {
  display: block;
  text-align: left;
}

/* 박스가 둘이라 사이 간격이 필요하다. 첫 박스는 공용 .boxArea 의 margin-top 을 그대로 쓴다. */
.popup.dark.assessment .boxArea + .boxArea {
  margin-top: 8px;
}

.titleArea {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-weight: 600;
}
</style>
